import { configManager } from "./ConfigManager";
import { createEncodedFilter, createEncodedParameters } from "./SaitUtils";

/**
 * Sincronizzazione su DB delle preferenze applicative salvate nel localStorage
 * (layout delle griglie, filtri salvati, tema, ...).
 *
 * Il localStorage resta la fonte usata dai componenti a runtime: il DB serve a renderlo
 * indipendente dalla postazione, cosi' piu' utenti sulla stessa macchina non si sovrascrivono
 * le preferenze a vicenda.
 *
 * Flusso:
 *  - al login si legge il record dell'utente: se esiste sovrascrive il localStorage,
 *    se non esiste si salva su DB quello attualmente in locale (retrocompatibilita');
 *  - ogni modifica successiva fa ripartire un salvataggio con debounce.
 */

export const PREFERENCES_MODULE = "PREFERENZE";
export const PREFERENCES_SAVE_DEBOUNCE_MS = 3000;

// Utente a cui appartengono le preferenze attualmente in localStorage: serve a non promuovere
// su DB le preferenze di un altro utente che ha usato la stessa postazione
export const PREFERENCES_OWNER_KEY = "framePreferencesOwner";

const FIND_PREFERENCES = "findFrmwsysutentiPrefs";
const SAVE_PREFERENCES = "saveRecordFrmcsysutentiPrefs";

let saveTimeoutId = null;
let syncAttempted = false; // la sincronizzazione va tentata una sola volta per sessione di pagina
let syncCompleted = false; // finche' non e' andata a buon fine non si scrive nulla su DB
let preferencesCleared = false; // dopo una cancellazione esplicita si congela tutto in attesa del reload
let currentToken = null;
let unloadListenersAttached = false;

function getAxiosInstance() {
	return configManager.getConfig("axiosInstance");
}

function getToken(token) {
	return token || currentToken || localStorage.getItem(configManager.getConfig("localStorageToken"));
}

/** Chiavi del localStorage che non sono preferenze utente e quindi non vanno sincronizzate */
function getExcludedKeys() {
	return [
		configManager.getConfig("localStorageToken"),
		configManager.getConfig("localStorageModule"),
		PREFERENCES_OWNER_KEY,
		...(configManager.getConfig("preferencesExcludedKeys") || []),
	].filter(Boolean);
}

export function isPreferenceKey(key) {
	return !!key && !getExcludedKeys().includes(key);
}

/** Snapshot di tutte le preferenze attualmente presenti nel localStorage */
export function readLocalPreferences() {
	const preferences = {};
	for (let index = 0; index < localStorage.length; index++) {
		const key = localStorage.key(index);
		if (isPreferenceKey(key)) {
			preferences[key] = localStorage.getItem(key);
		}
	}
	return preferences;
}

/** Sostituisce nel localStorage tutte le preferenze con quelle passate */
function writeLocalPreferences(preferences) {
	Object.keys(readLocalPreferences()).forEach((key) => localStorage.removeItem(key));
	Object.entries(preferences || {}).forEach(([key, value]) => {
		if (isPreferenceKey(key) && value !== null && value !== undefined) {
			localStorage.setItem(key, value);
		}
	});
}

async function fetchRemotePreferences(token) {
	const response = await getAxiosInstance().get(FIND_PREFERENCES, {
		headers: { Authorization: `Bearer ${getToken(token)}` },
		params: {
			filters: createEncodedFilter([], [{ property: "1", direction: "ASC" }], 1, 0),
			module: PREFERENCES_MODULE,
		},
	});
	return response?.data?.items?.[0]?.preferenze || null;
}

async function pushLocalPreferences(token) {
	await getAxiosInstance().post(
		SAVE_PREFERENCES,
		{
			parameters: createEncodedParameters({ preferenze: JSON.stringify(readLocalPreferences()) }),
			module: PREFERENCES_MODULE,
		},
		{ headers: { Authorization: `Bearer ${getToken(token)}` } },
	);
}

/**
 * Da chiamare subito dopo un login andato a buon fine, prima di renderizzare l'applicativo:
 * allinea il localStorage al record su DB dell'utente (o lo crea se non esiste ancora).
 */
export async function syncPreferencesOnLogin(token, username) {
	if (syncAttempted) {
		return;
	}
	syncAttempted = true;
	currentToken = token || null;

	try {
		const remotePreferences = await fetchRemotePreferences(token);

		if (remotePreferences) {
			let parsedPreferences = null;
			try {
				parsedPreferences = JSON.parse(remotePreferences);
			} catch (error) {
				console.error("Preferenze utente su DB non leggibili, mantengo quelle locali", error);
			}
			if (parsedPreferences) {
				writeLocalPreferences(parsedPreferences);
			}
			syncCompleted = true;
		} else {
			// Nessun record su DB: promuovo le preferenze locali solo se non appartengono a un altro
			// utente che ha usato la stessa postazione
			const localOwner = localStorage.getItem(PREFERENCES_OWNER_KEY);
			if (localOwner && username && localOwner !== username) {
				writeLocalPreferences({});
			}
			syncCompleted = true;
			await pushLocalPreferences(token);
		}

		if (username) {
			localStorage.setItem(PREFERENCES_OWNER_KEY, username);
		}
		attachUnloadListeners();
	} catch (error) {
		// In caso di errore si continua a lavorare solo sul localStorage, senza scrivere su DB
		syncCompleted = false;
		console.error("Sincronizzazione delle preferenze utente non riuscita", error);
	}
}

/** Da chiamare al logout, cosi' un eventuale nuovo login sulla stessa pagina risincronizza */
export function resetPreferencesSync() {
	cancelPendingSave();
	syncAttempted = false;
	syncCompleted = false;
	preferencesCleared = false;
	currentToken = null;
}

function cancelPendingSave() {
	if (saveTimeoutId) {
		clearTimeout(saveTimeoutId);
		saveTimeoutId = null;
	}
}

/** Programma il salvataggio su DB, accorpando le modifiche ravvicinate */
export function schedulePreferencesSave() {
	if (!syncCompleted || preferencesCleared) {
		return;
	}
	cancelPendingSave();
	saveTimeoutId = setTimeout(() => {
		saveTimeoutId = null;
		pushLocalPreferences().catch((error) => console.error("Salvataggio delle preferenze utente non riuscito", error));
	}, PREFERENCES_SAVE_DEBOUNCE_MS);
}

/** Esegue subito un eventuale salvataggio ancora in attesa */
export async function flushPreferencesSave() {
	if (!syncCompleted || saveTimeoutId === null) {
		return;
	}
	cancelPendingSave();
	try {
		await pushLocalPreferences();
	} catch (error) {
		console.error("Salvataggio delle preferenze utente non riuscito", error);
	}
}

function attachUnloadListeners() {
	if (unloadListenersAttached || typeof window === "undefined") {
		return;
	}
	unloadListenersAttached = true;
	// Se l'utente chiude o lascia la pagina entro il debounce, provo comunque a salvare
	window.addEventListener("pagehide", () => flushPreferencesSave());
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") {
			flushPreferencesSave();
		}
	});
}

/** Scrive una preferenza nel localStorage e programma l'allineamento su DB */
export function setPreference(key, value) {
	if (!key || preferencesCleared || localStorage.getItem(key) === value) {
		return; // nessuna modifica reale (o cancellazione in corso): evito salvataggi inutili
	}
	localStorage.setItem(key, value);
	schedulePreferencesSave();
}

/** Elimina una o piu' preferenze dal localStorage e programma l'allineamento su DB */
export function removePreferences(keys) {
	if (preferencesCleared) {
		return;
	}
	const toRemove = (Array.isArray(keys) ? keys : [keys]).filter((key) => key && localStorage.getItem(key) !== null);
	if (toRemove.length === 0) {
		return;
	}
	toRemove.forEach((key) => localStorage.removeItem(key));
	schedulePreferencesSave();
}

/**
 * Elimina tutte le preferenze dell'utente, sia in locale sia su DB.
 *
 * Su DB viene salvato un record vuoto invece di cancellare la riga: l'assenza del record significa
 * "utente mai sincronizzato" e al login successivo farebbe promuovere su DB le preferenze presenti
 * in locale, resuscitando proprio quelle appena eliminate.
 *
 * Ritorna true se anche il salvataggio su DB e' andato a buon fine.
 */
export async function clearAllPreferences() {
	cancelPendingSave();
	// Da qui in avanti i componenti ancora montati non devono piu' poter riscrivere nulla:
	// la pagina sta per essere ricaricata e ogni scrittura tardiva tornerebbe a galla
	preferencesCleared = true;
	writeLocalPreferences({});

	if (!syncCompleted) {
		return false;
	}

	try {
		await pushLocalPreferences();
		return true;
	} catch (error) {
		console.error("Cancellazione delle preferenze utente su DB non riuscita", error);
		return false;
	}
}
